import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { money } from "@owners/format";
import { ApiError, api } from "../api";
import { fmtDate, type Detail, type InputState, type Order } from "./ReceiverReports";

/**
 * Receivership (V009) — the Receiver's page. The receiver types the order
 * details once and supplies the monthly inputs (uploads from the accounting
 * system, or a note) and the lender advances log. The Special Servicer builds
 * the reports from these on Receiver Reports (OPEN_QUESTIONS A3, decided
 * 2026-10-26: Receivers supply inputs only).
 */

interface Mine {
  receiverships: Array<Order & { months: Array<{ period: string; label: string }> }>;
  hotelsWithout: Array<{ id: string; name: string }>;
  openRequests: Array<{ receivershipId: string; period: string; kind: string; accountKey: string; label: string; message: string | null; requestedAt: string; requestedBy: string | null }>;
}

export function Receivership() {
  const [params, setParams] = useSearchParams();
  const mine = useQuery({ queryKey: ["receivership"], queryFn: () => api<Mine>("/api/receivership") });
  const [setup, setSetup] = useState(false);
  const list = mine.data?.receiverships ?? [];
  const rid = params.get("id") ?? list[0]?.id ?? "";
  const period = params.get("period") ?? "";
  const pick = (id: string, p?: string) => setParams({ id, ...(p ? { period: p } : {}) });

  if (mine.isLoading) return <p className="muted">Loading…</p>;
  if (mine.error) return <p className="error">{(mine.error as Error).message}</p>;
  const m = mine.data!;
  return (
    <>
      <div className="eyebrow">Owners Module · Receivership</div>
      <h1>Receivership</h1>
      {m.openRequests.length > 0 && (
        <div className="card" style={{ borderColor: "var(--bad)" }}>
          <strong style={{ color: "var(--bad)" }}>Requested by the special servicer</strong>
          <ul className="agenda" style={{ marginTop: 6 }}>
            {m.openRequests.map((r, i) => {
              const rr = list.find((x) => x.id === r.receivershipId);
              return (
                <li key={i} style={{ cursor: "pointer" }} onClick={() => pick(r.receivershipId, r.period)}>
                  {r.label} · {rr?.months.find((x) => x.period === r.period)?.label ?? r.period} · {rr?.hotelName}
                  <span className="muted"> — {r.requestedBy ?? "Special servicer"}, {fmtDate(r.requestedAt)}{r.message ? `: ${r.message}` : ""}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <div className="row" style={{ marginBottom: 14 }}>
        {list.length > 0 && (
          <select value={rid} onChange={(e) => pick(e.target.value)}>
            {list.map((r) => <option key={r.id} value={r.id}>{r.hotelName} · {r.caseNumber}{r.status === "closed" ? " (closed)" : ""}</option>)}
          </select>
        )}
        {m.hotelsWithout.length > 0 && <button className="btn secondary" onClick={() => setSetup(true)}>+ Set up a receivership</button>}
      </div>
      {list.length === 0 && !setup && <div className="card"><p className="muted" style={{ margin: 0 }}>Set up the receivership with the order details to start sending monthly inputs.</p></div>}
      {setup && <OrderForm hotels={m.hotelsWithout} onDone={(id) => { setSetup(false); if (id) pick(id); }} />}
      {rid && !setup && <One key={rid} rid={rid} period={period} onPeriod={(p) => pick(rid, p)} />}
    </>
  );
}

function One({ rid, period, onPeriod }: { rid: string; period: string; onPeriod: (p: string) => void }) {
  const d = useQuery({ queryKey: ["receivership", rid, period], queryFn: () => api<Detail>(`/api/receivership/${rid}${period ? `?period=${period}` : ""}`) });
  const [editing, setEditing] = useState(false);
  if (d.isLoading) return <p className="muted">Loading…</p>;
  if (d.error) return <p className="error">{(d.error as Error).message}</p>;
  const x = d.data!;
  const o = x.receivership;
  return (
    <>
      <div className="card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2 style={{ margin: 0 }}>Order details</h2>
          {!editing && <button className="btn secondary" onClick={() => setEditing(true)}>Edit</button>}
        </div>
        {editing ? <OrderForm existing={o} onDone={() => setEditing(false)} /> : (
          <table style={{ marginTop: 8 }}><tbody>
            {([
              ["Hotel", o.hotelName ?? ""], ["Appointed", fmtDate(o.appointedOn)], ["Court", o.court], ["Case number", o.caseNumber], ["Caption", o.caseCaption ?? "—"],
              ["Receiver", `${o.receiverName}${o.receiverFirm ? `, ${o.receiverFirm}` : ""}`], ["Lender", o.lender], ["Special servicer", o.specialServicer ?? "—"],
              ["Report due", `Day ${o.dueDay} of the following month`],
              ["Receiver fee", o.receiverFeeMonthly == null ? "Not set" : `${money(o.receiverFeeMonthly)} per month${o.receiverFeeTerms ? ` · ${o.receiverFeeTerms}` : ""}`],
              ["Manager fee", `${(o.managerFeePct * 100).toFixed(1)}% of total revenue`],
              ["Receiver bank accounts", o.bankAccounts.length ? o.bankAccounts.map((b) => `${b.label}${b.bank ? ` (${b.bank}${b.last4 ? ` ··${b.last4}` : ""})` : ""}`).join("; ") : "None — add each account so a bank rec is tracked for it"],
              ...(o.status === "closed" ? [["Closed", fmtDate(o.closedOn)]] : []),
            ] as Array<[string, string]>).map(([k, v]) => <tr key={k}><td className="muted" style={{ width: 200 }}>{k}</td><td>{v}</td></tr>)}
          </tbody></table>
        )}
      </div>

      <div className="card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2 style={{ margin: 0 }}>Monthly inputs</h2>
          {x.months.length > 0 && (
            <div className="row">
              <select value={x.period ?? ""} onChange={(e) => onPeriod(e.target.value)}>
                {x.months.map((m) => <option key={m.period} value={m.period}>{m.label}</option>)}
              </select>
              {x.due && <span className="muted">due {fmtDate(x.due)}</span>}
            </div>
          )}
        </div>
        {!x.period ? <p className="muted">Inputs open once the appointment month ends.</p> : (
          <table style={{ marginTop: 8 }}>
            <thead><tr><th>Input</th><th>What's in</th><th style={{ width: 360 }}>Send</th></tr></thead>
            <tbody>{x.inputs.map((i) => <InputRowView key={i.kind + i.accountKey} rid={rid} period={x.period!} i={i} />)}</tbody>
          </table>
        )}
      </div>

      <Advances rid={rid} advances={x.advances} appointedOn={o.appointedOn} />
    </>
  );
}

function InputRowView({ rid, period, i }: { rid: string; period: string; i: InputState }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const file = useRef<HTMLInputElement>(null);
  const send = useMutation({
    mutationFn: async (override?: string) => {
      const n = override ?? note;
      const f = override ? undefined : file.current?.files?.[0];
      if (!f) return api(`/api/receivership/${rid}/inputs`, { method: "POST", body: { period, kind: i.kind, accountKey: i.accountKey, note: n } });
      // fields first, then the file (the API reads them in order)
      const fd = new FormData();
      fd.append("period", period); fd.append("kind", i.kind); fd.append("accountKey", i.accountKey); fd.append("note", n); fd.append("file", f);
      const r = await fetch(`/api/receivership/${rid}/inputs`, { method: "POST", credentials: "same-origin", headers: { "x-owners-app": "1" }, body: fd });
      const data = r.headers.get("content-type")?.includes("json") ? await r.json() : null;
      if (!r.ok) throw new ApiError(r.status, data?.error ?? `Upload failed (${r.status})`);
      return data;
    },
    onSuccess: () => { setNote(""); if (file.current) file.current.value = ""; qc.invalidateQueries({ queryKey: ["receivership"] }); },
  });
  const isAdv = i.kind === "advances";
  return (
    <tr>
      <td>{i.label}{!i.required && <span className="muted"> · optional</span>}
        {i.requestedAt && !i.received && <div><span className="pill bad-pill">Requested {fmtDate(i.requestedAt)}</span></div>}
      </td>
      <td>
        {i.received ? <span className="pill ok-pill">Received</span> : i.required ? <span className="pill warn-pill">Outstanding</span> : <span className="muted">—</span>}
        {i.input?.fileName && <div><a href={`/api/receivership-inputs/${i.input.id}/file`}>{i.input.fileName}</a></div>}
        {i.input?.note && <div className="muted" style={{ fontSize: 12 }}>{i.input.note}</div>}
        {isAdv && !!i.advancesInMonth && <div className="muted" style={{ fontSize: 12 }}>{i.advancesInMonth} advance{i.advancesInMonth === 1 ? "" : "s"} logged below</div>}
        {i.input && <div className="muted" style={{ fontSize: 11 }}>{i.input.submittedBy} · {fmtDate(i.input.submittedAt)}</div>}
      </td>
      <td>
        {isAdv ? (
          <button className="btn secondary" style={{ fontSize: 12 }} disabled={send.isPending || i.received}
            onClick={() => send.mutate("No lender advances this month.")}>
            {i.received ? "Log is complete" : "No advances this month"}
          </button>
        ) : (
          <div className="row" style={{ gap: 6 }}>
            <input type="file" ref={file} accept=".pdf,.xlsx,.xls,.csv,.docx,.doc,.jpg,.jpeg,.png" style={{ maxWidth: 190, fontSize: 12 }} />
            <input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 1, minWidth: 100, fontSize: 12 }} />
            <button className="btn" style={{ padding: "4px 10px", fontSize: 12 }} disabled={send.isPending} onClick={() => send.mutate(undefined)}>{i.input ? "Replace" : "Send"}</button>
          </div>
        )}
        {send.error && <div className="error" style={{ fontSize: 12 }}>{(send.error as Error).message}</div>}
      </td>
    </tr>
  );
}

function Advances({ rid, advances, appointedOn }: { rid: string; advances: Detail["advances"]; appointedOn: string }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ advancedOn: "", amount: "", purpose: "", reference: "" });
  const add = useMutation({
    mutationFn: () => api(`/api/receivership/${rid}/advances`, { method: "POST", body: f }),
    onSuccess: () => { setF({ advancedOn: "", amount: "", purpose: "", reference: "" }); qc.invalidateQueries({ queryKey: ["receivership"] }); },
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/api/receivership/advances/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["receivership"] }),
  });
  const total = advances.reduce((t, a) => t + Number(a.amount), 0);
  return (
    <div className="card">
      <h2>Lender protective advances</h2>
      <table>
        <thead><tr><th>Date</th><th>Purpose</th><th>Reference</th><th className="num">Amount</th><th /></tr></thead>
        <tbody>
          {advances.map((a) => (
            <tr key={a.id}><td>{fmtDate(a.advanced_on)}</td><td>{a.purpose}</td><td>{a.reference ?? ""}</td><td className="num">{money(a.amount, { decimals: 2 })}</td>
              <td className="num"><button className="btn link" onClick={() => { if (confirm("Remove this advance?")) del.mutate(a.id); }}>Remove</button></td></tr>
          ))}
          {advances.length > 0 && <tr className="total-row"><td colSpan={3}>Since appointment</td><td className="num">{money(total, { decimals: 2 })}</td><td /></tr>}
          <tr>
            <td><input type="date" min={appointedOn} value={f.advancedOn} onChange={(e) => setF({ ...f, advancedOn: e.target.value })} /></td>
            <td><input placeholder="e.g. Property taxes" value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} /></td>
            <td><input placeholder="Wire / check no." value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} /></td>
            <td className="num"><input style={{ width: 120, textAlign: "right" }} placeholder="$0.00" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></td>
            <td className="num"><button className="btn" disabled={add.isPending || !f.advancedOn || !f.amount || !f.purpose} onClick={() => add.mutate()}>Add</button></td>
          </tr>
        </tbody>
      </table>
      {(add.error || del.error) && <p className="error">{((add.error || del.error) as Error).message}</p>}
    </div>
  );
}

type OrderDraft = {
  hotelId: string; appointedOn: string; court: string; caseNumber: string; caseCaption: string; receiverName: string; receiverFirm: string;
  lender: string; specialServicer: string; dueDay: string; receiverFeeMonthly: string; receiverFeeTerms: string; managerFeePct: string;
  bankAccounts: Array<{ key?: string; label: string; bank: string; last4: string }>; status: "active" | "closed"; closedOn: string;
};

function OrderForm({ hotels = [], existing, onDone }: { hotels?: Array<{ id: string; name: string }>; existing?: Order; onDone: (id?: string) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<OrderDraft>(() => ({
    hotelId: hotels[0]?.id ?? "", appointedOn: existing?.appointedOn ?? "", court: existing?.court ?? "", caseNumber: existing?.caseNumber ?? "",
    caseCaption: existing?.caseCaption ?? "", receiverName: existing?.receiverName ?? "", receiverFirm: existing?.receiverFirm ?? "",
    lender: existing?.lender ?? "", specialServicer: existing?.specialServicer ?? "", dueDay: String(existing?.dueDay ?? 20),
    receiverFeeMonthly: existing?.receiverFeeMonthly == null ? "" : String(existing.receiverFeeMonthly), receiverFeeTerms: existing?.receiverFeeTerms ?? "",
    managerFeePct: String(((existing?.managerFeePct ?? 0.03) * 100).toFixed(2)).replace(/\.?0+$/, ""),
    bankAccounts: (existing?.bankAccounts ?? []).map((b) => ({ key: b.key, label: b.label, bank: b.bank ?? "", last4: b.last4 ?? "" })),
    status: existing?.status ?? "active", closedOn: existing?.closedOn ?? "",
  }));
  useEffect(() => { if (!f.hotelId && hotels[0]) setF((x) => ({ ...x, hotelId: hotels[0]!.id })); }, [hotels, f.hotelId]);
  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { ...f, receiverFeeMonthly: f.receiverFeeMonthly === "" ? null : f.receiverFeeMonthly, dueDay: Number(f.dueDay) };
      if (existing) { delete body.hotelId; if (f.status === "active") delete body.closedOn; }
      else { delete body.status; delete body.closedOn; }
      return existing ? api(`/api/receivership/${existing.id}`, { method: "PATCH", body }) : api<{ id: string }>("/api/receivership", { method: "POST", body });
    },
    onSuccess: (r: any) => { qc.invalidateQueries({ queryKey: ["receivership"] }); onDone(r?.id); },
  });
  const set = (k: keyof OrderDraft) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const field = (label: string, k: keyof OrderDraft, props: Record<string, unknown> = {}) => (
    <div className="field"><label>{label}</label><input value={f[k] as string} onChange={set(k)} {...props} /></div>
  );
  return (
    <div className={existing ? "" : "card"} style={{ marginTop: existing ? 10 : 0 }}>
      {!existing && <h2>Set up a receivership</h2>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        {!existing && (
          <div className="field"><label>Hotel</label>
            <select value={f.hotelId} onChange={set("hotelId")}>{hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}</select>
          </div>
        )}
        {field("Appointment date", "appointedOn", { type: "date" })}
        {field("Court", "court", { placeholder: "District Court, Hennepin County, Minnesota" })}
        {field("Case number", "caseNumber")}
        <div className="field" style={{ gridColumn: "1 / -1" }}><label>Case caption (court filing cover)</label><input value={f.caseCaption} onChange={set("caseCaption")} placeholder="Lender v. Borrower" /></div>
        {field("Receiver", "receiverName")}
        {field("Receiver's firm", "receiverFirm")}
        {field("Lender / trust", "lender")}
        {field("Special servicer", "specialServicer")}
        {field("Report due day (of the following month)", "dueDay", { type: "number", min: 1, max: 28 })}
        {field("Receiver fee per month ($)", "receiverFeeMonthly", { placeholder: "7,500" })}
        {field("Receiver fee terms (as in the order)", "receiverFeeTerms")}
        {field("Manager fee (% of total revenue)", "managerFeePct")}
      </div>
      <div className="field"><label>Receiver bank accounts (a bank rec is tracked for each — last four digits only)</label>
        {f.bankAccounts.map((b, i) => (
          <div key={i} className="row" style={{ marginBottom: 4 }}>
            <input placeholder="Label (Operating)" value={b.label} onChange={(e) => setF({ ...f, bankAccounts: f.bankAccounts.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
            <input placeholder="Bank" value={b.bank} onChange={(e) => setF({ ...f, bankAccounts: f.bankAccounts.map((x, j) => (j === i ? { ...x, bank: e.target.value } : x)) })} />
            <input placeholder="Last 4" maxLength={4} style={{ width: 70 }} value={b.last4} onChange={(e) => setF({ ...f, bankAccounts: f.bankAccounts.map((x, j) => (j === i ? { ...x, last4: e.target.value.replace(/\D/g, "") } : x)) })} />
            <button className="btn link" onClick={() => setF({ ...f, bankAccounts: f.bankAccounts.filter((_, j) => j !== i) })}>Remove</button>
          </div>
        ))}
        <div><button className="btn link" onClick={() => setF({ ...f, bankAccounts: [...f.bankAccounts, { label: "", bank: "", last4: "" }] })}>+ Add account</button></div>
      </div>
      {existing && (
        <div className="row" style={{ marginBottom: 10 }}>
          <label><input type="checkbox" checked={f.status === "closed"} onChange={(e) => setF({ ...f, status: e.target.checked ? "closed" : "active" })} /> Receivership closed</label>
          {f.status === "closed" && <input type="date" value={f.closedOn} min={f.appointedOn} onChange={set("closedOn")} />}
        </div>
      )}
      {save.error && <p className="error">{(save.error as Error).message}</p>}
      <div className="row">
        <button className="btn gold" disabled={save.isPending} onClick={() => save.mutate()}>{existing ? "Save" : "Set up"}</button>
        <button className="btn secondary" onClick={() => onDone()}>Cancel</button>
      </div>
    </div>
  );
}
